import { useTranslation } from"react-i18next";
import { useState, useEffect, useRef } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Receipt, Plus, Clock, CheckCircle, XCircle, AlertCircle, Upload, X, Paperclip } from"lucide-react";
import { toast } from"sonner";

const ContractorInvoicesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [invoices, setInvoices] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [hasApprovedReport, setHasApprovedReport] = useState(false);
 const [approvedReports, setApprovedReports] = useState([]);
 const [uploadingFiles, setUploadingFiles] = useState(false);
 const fileInputRef = useRef(null);
 const [formData, setFormData] = useState({
 linked_report_id:"",
 m2_completed:"",
 price_per_m2_used: null,
 description:"",
 amount:"",
 tax_amount:"",
 due_date:"",
 invoice_attachments:[],
 m2_locked: false,
 material_unit_of_measure:"m\u00B2",
 engineer_adjusted: false
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const [dashboardRes, invoicesRes, reportsRes] = await Promise.all([
 axios.get("/dashboard/contractor"),
 axios.get("/contractor/invoices"),
 axios.get("/contractor/progress-reports")
 ]);
 setDashboard(dashboardRes.data);
 const allInvoices = invoicesRes.data;
 setInvoices(allInvoices);
 // Approved reports not already invoiced (non-rejected)
 const invoicedReportIds = new Set(
 allInvoices.filter(i => i.status !== "rejected" && i.linked_report_id).map(i => i.linked_report_id)
 );
 const approved = reportsRes.data.filter(r => r.status === "approved" && !invoicedReportIds.has(r.id));
 setApprovedReports(approved);
 setHasApprovedReport(reportsRes.data.some(r => r.status === "approved"));
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleReportSelect = async (reportId) => {
 const report = approvedReports.find(r => r.id === reportId);
 // Try to find a matching accepted offer with per_m2 pricing
 const offers = dashboard?.my_offers || [];
 const matchingOffer = offers.find(o =>
 o.status === "accepted" &&
 o.pricing_type === "per_m2" &&
 o.price_per_m2 > 0 && (
 (report?.task_id && (o.task_ids?.includes(report.task_id) || o.main_task_id === report.task_id)) ||
 o.service_type === report?.milestone_label?.split(" — ")[0]
 )
 ) || (offers.find(o => o.status === "accepted" && o.pricing_type === "per_m2" && o.price_per_m2 > 0));
 const pricePerM2 = matchingOffer?.price_per_m2 || null;

 // Auto-fill m² from approved report's material data
 let reportM2 = report?.material_total || 0;
 let uom = report?.material_unit_of_measure || "m\u00B2";
 let adjusted = report?.engineer_adjusted_quantities || false;

 // Fallback: if report has no material_total but has a task, try fetching from the backend
 if (!reportM2 && report?.task_id) {
 try {
 const { data } = await axios.get(`/job-materials/${report.task_id}`);
 const items = data.items || [];
 if (items.length > 0) {
 // For by_floor: filter by the floor in the milestone_key
 if (report.milestone_type === "by_floor" && report.milestone_key) {
 const parts = report.milestone_key.split(":floor:");
 if (parts.length === 2) {
 const floorStr = parts[1].split("_").pop();
 const floorNum = parseInt(floorStr);
 const floorItems = items.filter(it => it.floor === floorNum);
 reportM2 = floorItems.reduce((s, it) => s + (it.quantity || 0), 0);
 }
 } else {
 reportM2 = items.reduce((s, it) => s + (it.quantity || 0), 0);
 }
 reportM2 = Math.round(reportM2 * 100) / 100;
 if (items[0]?.unit_of_measure) uom = items[0].unit_of_measure;
 }
 } catch {}
 }

 const hasReportMaterial = reportM2 > 0;
 const autoAmount = hasReportMaterial && pricePerM2 ? (reportM2 * pricePerM2).toFixed(2) : "";
 setFormData(prev => ({
 ...prev,
 linked_report_id: reportId,
 price_per_m2_used: pricePerM2,
 m2_completed: hasReportMaterial ? String(reportM2) : "",
 m2_locked: hasReportMaterial, // lock the field when material data exists
 material_unit_of_measure: uom,
 engineer_adjusted: adjusted,
 amount: autoAmount || prev.amount
 }));
 };

 const handleM2Change = (value) => {
 const m2 = parseFloat(value) || 0;
 const price = formData.price_per_m2_used;
 setFormData(prev => ({
 ...prev,
 m2_completed: value,
 amount: price && m2 > 0 ? (m2 * price).toFixed(2) : prev.amount
 }));
 };

 const handleFileUpload = async (e) => {
 const files = Array.from(e.target.files || []);
 if (!files.length) return;
 setUploadingFiles(true);
 try {
 const uploaded = await Promise.all(files.map(async (file) => {
 const fd = new FormData();
 fd.append("file", file);
 const res = await axios.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
 return { url: res.data.url, filename: res.data.filename || file.name };
 }));
 setFormData(prev => ({ ...prev, invoice_attachments: [...prev.invoice_attachments, ...uploaded] }));
 toast.success(`${uploaded.length} file(s) uploaded`);
 } catch { toast.error("Upload failed"); }
 finally { setUploadingFiles(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formData.linked_report_id) {
 toast.error("Please select an approved report to link to this invoice");
 return;
 }
 if (!formData.amount) {
 toast.error(t("validation.required"));
 return;
 }
 const selectedReport = approvedReports.find(r => r.id === formData.linked_report_id);
 setSubmitting(true);
 try {
 const assignment = dashboard?.assignments?.[0];
 await axios.post("/contractor/invoices", {
 project_id: assignment?.project_id,
 task_id: selectedReport?.task_id || null,
 linked_report_id: formData.linked_report_id,
 linked_report_title: selectedReport?.title || "",
 m2_completed: formData.m2_completed ? parseFloat(formData.m2_completed) : null,
 price_per_m2_used: formData.price_per_m2_used || null,
 description: formData.description || null,
 amount: parseFloat(formData.amount) || 0,
 tax_amount: parseFloat(formData.tax_amount) || 0,
 due_date: formData.due_date || null,
 invoice_attachments: formData.invoice_attachments.map(a => a.url)
 });
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({ linked_report_id:"", m2_completed:"", price_per_m2_used: null, description:"", amount:"", tax_amount:"", due_date:"", invoice_attachments:[], m2_locked: false, material_unit_of_measure:"m\u00B2", engineer_adjusted: false });
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 } finally {
 setSubmitting(false);
 }
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case"approved":
 return <CheckCircle className="w-5 h-5 text-green-500"/>;
 case"rejected":
 return <XCircle className="w-5 h-5 text-red-500"/>;
 case"pending_accountant":
 return <AlertCircle className="w-5 h-5 text-blue-600"/>;
 default:
 return <Clock className="w-5 h-5 text-yellow-500"/>;
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case"approved":
 return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 case"rejected":
 return"bg-rose-500/10 text-rose-700 border-red-200/60";
 case"pending_accountant":
 return"bg-blue-500/10 text-blue-700 border-blue-200/60";
 default:
 return"bg-amber-500/10 text-amber-700 border-yellow-200/60";
 }
 };

 const getStatusLabel = (status) => {
 switch (status) {
 case"pending_engineer":
 return"Pending Engineer";
 case"pending_accountant":
 return"Pending Accountant";
 case"approved":
 return"Approved";
 case"rejected":
 return"Rejected";
 default:
 return status;
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading...</div>
 </div>
 </Layout>
 );
 }

 const totalAmount = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
 const approvedAmount = invoices.filter(i => i.status ==="approved").reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
 const pendingAmount = invoices.filter(i => i.status !=="approved"&& i.status !=="rejected").reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 My Invoices
 </h1>
 <p className="text-slate-600">
 Create and track invoices for your work
 </p>
 </div>
 <Button
 onClick={() => hasApprovedReport ? setDialogOpen(true) : toast.error("You need an approved progress report before creating an invoice.")}
 className={hasApprovedReport ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-300 cursor-not-allowed"}
 data-testid="new-invoice-btn"
 >
 <Plus className="w-4 h-4 mr-2"/>
 New Invoice
 </Button>
 </div>

 {/* Invoice Gate Banner */}
 {!hasApprovedReport && (
 <Card className="border-amber-200 bg-amber-50">
 <CardContent className="p-4 flex items-start gap-3">
 <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
 <div>
 <p className="font-semibold text-amber-800 text-sm">Progress report required before invoicing</p>
 <p className="text-sm text-amber-700 mt-0.5">
 Submit a progress report via <strong>Engineer Reports</strong> and wait for engineer approval. Once approved, you'll be able to create invoices.
 </p>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 <Card className="">
 <CardContent className="p-4">
 <p className="text-sm text-slate-600">Total Invoiced</p>
 <p className="text-2xl font-bold text-slate-900">€{totalAmount.toLocaleString()}</p>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="p-4">
 <p className="text-sm text-slate-500">Approved</p>
 <p className="text-2xl font-bold text-slate-900">€{approvedAmount.toLocaleString()}</p>
 </CardContent>
 </Card>
 <Card className="">
 <CardContent className="p-4">
 <p className="text-sm text-slate-500">Pending Approval</p>
 <p className="text-2xl font-bold text-slate-900">€{pendingAmount.toLocaleString()}</p>
 </CardContent>
 </Card>
 </div>

 {/* Approval Flow Info */}
 <Card className="">
 <CardContent className="p-4">
 <h4 className="font-semibold text-slate-900 mb-2">Invoice Approval Process</h4>
 <div className="flex items-center gap-2 text-sm">
 <Badge variant="outline"className="bg-amber-500/10 text-amber-700">1. Pending Engineer</Badge>
 <span>→</span>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700">2. Pending Accountant</Badge>
 <span>→</span>
 <Badge variant="outline"className="bg-emerald-500/10 text-emerald-700">3. Approved</Badge>
 </div>
 </CardContent>
 </Card>

 {/* Invoices List */}
 {invoices.length > 0 ? (
 <div className="space-y-4">
 {invoices.map((invoice) => (
 <Card key={invoice.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-4">
 {getStatusIcon(invoice.status)}
 <div>
 <div className="flex items-center gap-2">
 <h3 className="font-semibold text-slate-900">{invoice.invoice_number}</h3>
 <Badge variant="outline"className={getStatusColor(invoice.status)}>
 {getStatusLabel(invoice.status)}
 </Badge>
 </div>
 <p className="text-sm text-slate-600 mt-1">{invoice.description}</p>
 <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
 <span>Created: {new Date(invoice.created_at).toLocaleDateString()}</span>
 {invoice.due_date && <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>}
 </div>
 {invoice.engineer_approved && (
 <div className="mt-2 text-xs text-green-600">
 ✓ Engineer approved on {new Date(invoice.engineer_approved_at).toLocaleDateString()}
 </div>
 )}
 {invoice.rejection_reason && (
 <div className="mt-2 p-2 bg-red-50/80 rounded text-sm text-red-700">
 <span className="font-medium">Rejection Reason:</span> {invoice.rejection_reason}
 </div>
 )}
 {invoice.m2_completed && (
 <div className="mt-2 text-xs text-blue-600">
 {invoice.m2_completed} {invoice.material_unit_of_measure || "m\u00B2"}
 {invoice.report_material_total > 0 && " (verified from report)"}
 </div>
 )}
 </div>
 </div>
 <div className="text-right">
 <p className="text-xl font-bold text-slate-900">
 €{invoice.total_amount?.toLocaleString()}
 </p>
 <p className="text-[11px] text-slate-500">
 (€{invoice.amount?.toLocaleString()} + €{invoice.tax_amount?.toLocaleString()} tax)
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <Card className="">
 <CardContent className="text-center py-12">
 <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600 mb-4">No invoices created yet</p>
 <Button onClick={() => hasApprovedReport ? setDialogOpen(true) : toast.error("You need an approved progress report before creating an invoice.")}>
 <Plus className="w-4 h-4 mr-2"/>
 Create Your First Invoice
 </Button>
 </CardContent>
 </Card>
 )}

 {/* New Invoice Dialog */}
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle className="font-display uppercase tracking-tight text-slate-900">Create New Invoice</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">

 {/* Approved Report (required) */}
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
 Approved Report <span className="text-red-500">*</span>
 </Label>
 <Select
 value={formData.linked_report_id}
 onValueChange={handleReportSelect}
 >
 <SelectTrigger className="mt-1 rounded-sm">
 <SelectValue placeholder="Select an approved report…"/>
 </SelectTrigger>
 <SelectContent>
 {approvedReports.map((r) => (
 <SelectItem key={r.id} value={r.id}>
 {r.title}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 <p className="text-[11px] text-slate-400 mt-1">Only approved reports are shown</p>
 </div>

 {/* m² completed + price per m² */}
 <div>
 <div className="flex flex-wrap items-center gap-1 mb-1">
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
 {formData.material_unit_of_measure || "m\u00B2"} Completed
 {!formData.m2_locked && <span className="text-slate-400 font-normal text-xs ml-1">(optional)</span>}
 {formData.m2_locked && <span className="text-blue-500 font-normal text-xs ml-1">(from approved report)</span>}
 </Label>
 {formData.price_per_m2_used && (
 <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 whitespace-nowrap">
 €{formData.price_per_m2_used}/{formData.material_unit_of_measure || "m\u00B2"}
 </span>
 )}
 </div>
 <Input
 className={`rounded-sm ${formData.m2_locked ? "bg-slate-100 cursor-not-allowed" : ""}`}
 type="number"
 step="0.01"
 placeholder="e.g. 352"
 value={formData.m2_completed}
 onChange={(e) => !formData.m2_locked && handleM2Change(e.target.value)}
 readOnly={formData.m2_locked}
 data-testid="m2-completed-input"
 />
 {formData.m2_locked && (
 <p className="text-xs text-blue-600 mt-1">
 This value is set from the approved progress report{formData.engineer_adjusted ? " (adjusted by engineer)" : ""} and cannot be changed.
 </p>
 )}
 {formData.price_per_m2_used && formData.m2_completed && (
 <p className="text-xs text-emerald-600 mt-1">
 {formData.m2_completed} {formData.material_unit_of_measure || "m\u00B2"} × €{formData.price_per_m2_used}/{formData.material_unit_of_measure || "m\u00B2"} = <strong>€{(parseFloat(formData.m2_completed) * formData.price_per_m2_used).toFixed(2)}</strong> — amount auto-filled below
 </p>
 )}
 {!formData.price_per_m2_used && !formData.m2_locked && (
 <p className="text-[11px] text-slate-400 mt-1">Enter {formData.material_unit_of_measure || "m\u00B2"} to document quantity. Set price/{formData.material_unit_of_measure || "m\u00B2"} on the offer to auto-calculate amount.</p>
 )}
 </div>

 {/* Amount + Tax */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount (€) <span className="text-red-500">*</span></Label>
 <Input className="mt-1 rounded-sm" type="number" step="0.01"
 value={formData.amount}
 onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
 placeholder="0.00" />
 </div>
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Tax Amount (€)</Label>
 <Input className="mt-1 rounded-sm" type="number" step="0.01"
 value={formData.tax_amount}
 onChange={(e) => setFormData(prev => ({ ...prev, tax_amount: e.target.value }))}
 placeholder="0.00" />
 </div>
 </div>

 {/* Total Preview */}
 <div className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
 <span className="text-sm text-slate-600">Total Amount:</span>
 <span className="font-bold text-slate-900 text-lg">
 €{((parseFloat(formData.amount) || 0) + (parseFloat(formData.tax_amount) || 0)).toLocaleString()}
 </span>
 </div>

 {/* Due Date */}
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Due Date</Label>
 <Input className="mt-1 rounded-sm" type="date"
 value={formData.due_date}
 onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} />
 </div>

 {/* Notes (optional) */}
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
 Notes <span className="text-slate-400 font-normal text-xs">(optional)</span>
 </Label>
 <Textarea className="mt-1 rounded-sm text-sm" rows={2}
 value={formData.description}
 onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
 placeholder="Any additional notes for the engineer..." />
 </div>

 {/* Invoice File Upload */}
 <div>
 <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
 Invoice Document <span className="text-slate-400 font-normal text-xs">(PDF or photo)</span>
 </Label>
 <label className="mt-1 flex flex-col items-center justify-center w-full border-2 border-dashed border-slate-200 rounded-sm p-3 cursor-pointer hover:border-slate-300 transition-colors">
 <Upload className="w-4 h-4 text-slate-400 mb-1" />
 <span className="text-xs text-slate-500">{uploadingFiles ? "Uploading…" : "Click to upload PDF or photo"}</span>
 <input ref={fileInputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
 onChange={handleFileUpload} disabled={uploadingFiles} />
 </label>
 {formData.invoice_attachments.length > 0 && (
 <div className="mt-2 space-y-1">
 {formData.invoice_attachments.map((a, i) => (
 <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1">
 <div className="flex items-center gap-1.5">
 <Paperclip className="w-3 h-3 text-slate-400" />
 <span className="truncate text-slate-600">{a.filename}</span>
 </div>
 <button type="button" onClick={() => setFormData(prev => ({ ...prev, invoice_attachments: prev.invoice_attachments.filter((_, j) => j !== i) }))}
 className="text-slate-400 hover:text-red-500 ml-2">
 <X className="w-3 h-3" />
 </button>
 </div>
 ))}
 </div>
 )}
 </div>

 <div className="flex gap-3 justify-end pt-2 border-t">
 <Button type="button" variant="outline" className="rounded-sm" onClick={() => setDialogOpen(false)}>
 Cancel
 </Button>
 <Button type="submit" disabled={submitting} className="rounded-sm bg-slate-900 hover:bg-slate-800">
 {submitting ? "Submitting…" : "Submit Invoice"}
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default ContractorInvoicesPage;
